import React, { useEffect, useState } from 'react';
import { Field } from '../../components/ui/Field';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { Icon } from '../../components/ui/Icon';

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  'pk_test_51OjkxjARBxBVjrZfPgpG90XZn0qn1I01HYfhDJIgAGVnzGvRv3TgAiAXeYnxbZZXjrZ42A5H3ybnwgQgV9MU0FAc00GGi84xYi';

const DynamicCardForm: React.FC<{
  onCardAdded: (success: boolean, errorMessage?: string) => void;
  billingName: string;
  stripe: any;
}> = ({ onCardAdded, billingName, stripe }) => {
  const [focused, setFocused] = useState(false);
  const [complete, setComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [cardElementReady, setCardElementReady] = useState(false);

  useEffect(() => {
    let cardElementInitialized = false;

    const setupCardElement = async () => {
      if (cardElementInitialized) return;
      try {
        const container = document.getElementById('shipping-card-element-container');
        if (!container) return;
        container.innerHTML = '';
        const cardElementDiv = document.createElement('div');
        cardElementDiv.id = 'shipping-card-element';
        cardElementDiv.style.width = '100%';
        container.appendChild(cardElementDiv);

        const cardElement = stripe.elements().create('card', {
          style: {
            base: {
              fontSize: '15px',
              color: '#0d1027',
              fontFamily: 'Inter, system-ui, sans-serif',
              '::placeholder': { color: '#9a9fb8' },
              iconColor: '#2b55ff',
            },
            invalid: {
              color: '#c33c2e',
              iconColor: '#c33c2e',
            },
          },
          hidePostalCode: true,
        });

        cardElement.mount('#shipping-card-element');
        cardElement.on('change', (event: any) => {
          setComplete(event.complete);
          setCardError(event.error ? event.error.message : null);
        });
        cardElement.on('focus', () => setFocused(true));
        cardElement.on('blur', () => setFocused(false));

        cardElementInitialized = true;
        setCardElementReady(true);
      } catch (err) {
        console.error('Error setting up card element:', err);
        setCardError('Failed to set up card element. Please try again.');
      }
    };

    if (stripe) setupCardElement();
  }, [stripe]);

  const handleGenerateToken = async () => {
    if (!stripe || !cardElementReady || !billingName) return;
    setProcessing(true);
    try {
      const cardElement = stripe.elements().getElement('card');
      if (!cardElement) throw new Error('Card element not found');
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { name: billingName },
      });
      if (error) throw new Error(error.message || 'Failed to process card');
      if (!paymentMethod) throw new Error('No payment method returned');
      onCardAdded(true, paymentMethod.id);
    } catch (err) {
      onCardAdded(false, err instanceof Error ? err.message : 'Card processing failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div
        style={{
          padding: 14,
          border: `1px solid ${focused ? 'var(--brand-blue)' : cardError ? 'var(--err)' : 'var(--line-strong)'}`,
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-elev)',
          transition: 'border-color 0.15s ease',
          marginBottom: 12,
        }}
      >
        <div id="shipping-card-element-container" style={{ width: '100%' }} />
      </div>
      {cardError && (
        <div className="muted" style={{ color: 'var(--err)', fontSize: 12, marginBottom: 10 }}>
          {cardError}
        </div>
      )}
      {complete && !cardError && (
        <div style={{ color: 'var(--ok)', fontSize: 12, marginBottom: 10 }}>
          <Icon name="checkCircle" size={12} /> Card information is complete
        </div>
      )}
      <Button
        variant="primary"
        block
        disabled={processing || !complete || !billingName}
        onClick={handleGenerateToken}
      >
        <Icon name="card" size={14} />
        <span>{processing ? 'Processing…' : 'Save card details'}</span>
      </Button>
    </div>
  );
};

export const StripeCardForm: React.FC<{
  onCardAdded: (success: boolean, errorMessage?: string) => void;
  setBillingName: (name: string) => void;
  billingName: string;
}> = ({ onCardAdded, setBillingName, billingName }) => {
  const [loading, setLoading] = useState(true);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripe, setStripe] = useState<any>(null);

  useEffect(() => {
    let mounted = true;
    const loadStripe = async () => {
      try {
        const stripeJs = await import('@stripe/stripe-js');
        const stripePromise = stripeJs.loadStripe(STRIPE_PUBLISHABLE_KEY);
        const _stripe = await stripePromise;
        if (mounted) {
          setStripe(_stripe);
          setStripeLoaded(true);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load Stripe:', err);
        if (mounted) {
          setError('Failed to load payment processor. Please try again later.');
          setLoading(false);
        }
      }
    };
    loadStripe();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: 24 }}>
        Loading payment processor…
      </div>
    );
  }

  if (error) {
    return (
      <Callout tone="warn">
        <Icon name="alertCircle" size={16} />
        <span>{error}</span>
      </Callout>
    );
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <Field label="Cardholder name">
        <Input
          value={billingName}
          onChange={(e) => setBillingName(e.target.value)}
          required
        />
      </Field>
      <div style={{ marginTop: 12 }}>
        {stripeLoaded && (
          <DynamicCardForm
            onCardAdded={onCardAdded}
            billingName={billingName}
            stripe={stripe}
          />
        )}
      </div>
    </div>
  );
};

export default StripeCardForm;
